<template>
  <article class="flex flex-col h-full">
    <nav class="flex items-center justify-between p-4 pb-0 lg:px-12">
      <label class="flex items-center mr-3" for="dpa-lists">
        <div class="relative print:border-b-0 print:-mx-6">
          <svg
            class="absolute -mt-3 top-1/2 print:hidden"
            fill="none"
            height="19"
            viewBox="0 0 20 18"
            width="20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 10.7554V15.0684C2 15.3064 2.224 15.5004 2.5 15.5004H17.5C17.776 15.5004 18 15.3064 18 15.0684V6.4784C18 6.2394 17.776 6.0464 17.5 6.0464H10C9.7 6.0464 9.416 5.9114 9.226 5.6784L6.626 2.5004H2.5C2.224 2.5004 2 2.6934 2 2.9314V10.7554ZM17.5 17.5004H2.5C1.122 17.5004 0 16.4094 0 15.0684V2.9314C0 1.5904 1.122 0.500397 2.5 0.500397H7.101C7.4 0.500397 7.685 0.634397 7.875 0.867397L10.474 4.0464H17.5C18.878 4.0464 20 5.1364 20 6.4784V15.0684C20 16.4094 18.878 17.5004 17.5 17.5004Z"
              fill="#212121"
            />
          </svg>
          <select
            class="px-8 text-lg truncate transition duration-150 bg-transparent bg-center bg-right bg-no-repeat border-0 border-b rounded-none appearance-none cursor-pointer print:border-b-0 sm:text-xl md:text-2xl hover:border-gray-700"
            v-model="activeFolderId"
            name="dpa-sidebar-lists"
            id="dpa-lists"
            style="
              width: 45vw;
              background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij4KICA8ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPgogICAgPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0Ii8+CiAgICA8cGF0aCBmaWxsPSIjNzU3NTc1IiBmaWxsLXJ1bGU9Im5vbnplcm8iIGQ9Ik0xMiwxNS41IEMxMS43NDQsMTUuNSAxMS40ODgsMTUuNDAyIDExLjI5MywxNS4yMDcgTDcuMjkzLDExLjIwNyBDNi45MDIsMTAuODE2IDYuOTAyLDEwLjE4NCA3LjI5Myw5Ljc5MyBDNy42ODQsOS40MDIgOC4zMTYsOS40MDIgOC43MDcsOS43OTMgTDEyLjAxMiwxMy4wOTggTDE1LjMwNSw5LjkxOCBDMTUuNzA0LDkuNTM1IDE2LjMzNSw5LjU0NiAxNi43MTksOS45NDMgQzE3LjEwMywxMC4zNCAxNy4wOTIsMTAuOTc0IDE2LjY5NSwxMS4zNTcgTDEyLjY5NSwxNS4yMTkgQzEyLjUsMTUuNDA3IDEyLjI1LDE1LjUgMTIsMTUuNSIvPgogIDwvZz4KPC9zdmc+Cg==');
            "
          >
            <option disabled value="">Bitte wählen</option>
            <option
              v-for="folder in folders"
              :value="folder.id"
              :key="folder.id"
            >
              {{ folder.name }}
            </option>
          </select>
        </div>
      </label>
      <folder-navigation
        class="block print:hidden"
        :folder-id="folderId"
        :show-choose-folder-template="
          dossiers &&
          dossiers.folderTemplates &&
          dossiers.folderTemplates.length > 0
        "
      ></folder-navigation>
    </nav>
    <div class="relative flex-grow">
      <container
        :animation-duration="50"
        :drop-placeholder="{ className: 'bg-gray-300 mr-4' }"
        :get-child-payload="getChildPayload"
        @drop="onDrop"
        class="absolute inset-0 flex h-full p-4 overflow-x-auto print:flex-col print:overflow-visible lg:px-12"
        group-name="folder"
        lock-axis="x"
        orientation="horizontal"
      >
        <component
          v-for="(list, index) in visualFolderLists"
          :is="currentDraggable(index)"
          :key="`draggable-${list.id}-${index}`"
          class="inline-block h-full pr-4 whitespace-no-wrap align-top custom-column-width print:shadow-none print:w-4/5"
        >
          <folder-list
            class="print:shadow-none"
            :class="{ 'cursor-pointer': index !== 0 }"
            :id="list.id"
            :name="list.name"
            :folder-id="folderId"
            :key="`list-${list.id}`"
            :is-root="index == 0"
            @openValueUnitDetail="valueUnitDetailOpen = true"
            @fetchValueUnitDetailData="fetchValueUnitDetailData"
          ></folder-list>
        </component>

        <div
          class="pr-4 whitespace-no-wrap align-top print:hidden lg:pr-12 custom-column-width"
        >
          <router-link
            :to="{ name: 'addFolderList' }"
            class="flex items-center w-full p-3 pr-2 mb-3 text-xs font-medium text-gray-700 border border-gray-300 hover:border-gray-500 hover:text-gray-900"
          >
            <svg
              class="mr-2"
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.6667 5.33332H6.66667V1.33332C6.66667 0.964657 6.368 0.666656 6 0.666656C5.632 0.666656 5.33334 0.964657 5.33334 1.33332V5.33332H1.33334C0.965338 5.33332 0.666672 5.63132 0.666672 5.99999C0.666672 6.36866 0.965338 6.66666 1.33334 6.66666H5.33334V10.6667C5.33334 11.0353 5.632 11.3333 6 11.3333C6.368 11.3333 6.66667 11.0353 6.66667 10.6667V6.66666H10.6667C11.0347 6.66666 11.3333 6.36866 11.3333 5.99999C11.3333 5.63132 11.0347 5.33332 10.6667 5.33332Z"
                fill="#757575"
              />
            </svg>
            Weitere Liste hinzufügen
          </router-link>
        </div>
      </container>
    </div>

    <ui-text-input-dialog
      :heading="'Mappe umbenennen'"
      :label="'Name'"
      :value="activeFolderName"
      @cancel="showFolder"
      @submit="handleRenameFolder"
      v-if="action == 'edit'"
    />
    <choose-folder-template-dialog
      @submit="handleChooseFolderTemplate"
      @cancel="showFolder"
      :folder-templates="dossiers && dossiers.folderTemplates"
      v-if="action == 'chooseFolderTemplate'"
    >
    </choose-folder-template-dialog>
    <ui-confirmation-dialog
      @confirm="handleDeleteFolder()"
      @cancel="showFolder"
      v-if="action == 'confirm'"
    >
      Möchten Sie die Mappe <strong>{{ activeFolderName }}</strong> wirklich
      löschen? Gelöschte Mappen können nicht wiederhergestellt werden.
    </ui-confirmation-dialog>
    <ui-text-input-dialog
      :heading="'Liste hinzufügen'"
      :label="'Name'"
      @cancel="showFolder"
      @submit="handleAddFolderList"
      v-if="action == 'addFolderList'"
    />
    <ui-text-input-dialog
      :heading="'Liste umbenennen'"
      :label="'Name'"
      :value="formatDefaultFolderName(activeFolderListName)"
      @cancel="showFolder"
      @submit="handleRenameFolderList"
      v-if="action == 'editFolderList'"
    />
    <ui-confirmation-dialog
      @confirm="handleDeleteFolderList(folderListId)"
      @cancel="showFolder"
      v-if="action == 'confirmDeleteFolderList'"
    >
      Möchten Sie die Liste
      <strong>{{ formatDefaultFolderName(activeFolderListName) }}</strong>
      wirklich löschen? Gelöschte Listen können nicht wiederhergestellt werden.
    </ui-confirmation-dialog>

    <ui-modal v-if="valueUnitDetailOpen" @cancel="closeValueUnitDetail">
      <value-unit-detail-view
        v-bind="valueUnitDetailProps"
        @delete="onValueUnitDetailDelete"
      />
    </ui-modal>
  </article>
</template>
<script>
import { computed, defineComponent, ref } from "vue";
import collab from "../collab";
import ChooseFolderTemplateDialog from "../components/ChooseFolderTemplateDialog.vue";
import FolderList from "../components/FolderList.vue";
import FolderNavigation from "../components/FolderNavigation.vue";
import UiConfirmationDialog from "../components/UiConfirmationDialog.vue";
import UiDialog from "../components/UiDialog.vue";
import UiTextInputDialog from "../components/UiTextInputDialog.vue";
import format from "date-fns/format";
import locale from "date-fns/locale/de";
import { Container, Draggable } from "vue-smooth-dnd";
import { EventBus } from "../eventBus";
import UiModal from "../components/UiModal";
import ValueUnitDetailView from "../components/ValueUnitDetailView";
import { useValueUnitDetail } from "../compositions/useValueUnitDetail";
import { formatDefaultFolderName } from "../compositions/formatDefaultFolderName";

const appendToFolderListQuery = require("../graphql/appendToFolderList.gql");
const applyFolderTemplateToFolderQuery = require("../graphql/applyFolderTemplateToFolder.gql");
const deleteFolderQuery = require("../graphql/deleteFolder.gql");
const deleteFolderItemQuery = require("../graphql/deleteFolderItem.gql");
const getFolderTemplatesQuery = require("../graphql/getFolderTemplates.gql");
const moveFolderItemQuery = require("../graphql/moveFolderItem.gql");
const renameFolderQuery = require("../graphql/renameFolder.gql");
const renameFolderListQuery = require("../graphql/renameFolderList.gql");

export default defineComponent({
  setup(props) {
    const { data: folders, execute: fetchFolders } =
      collab.DpaIdCollabGraphqlUseFolders(true);

    const { data: folderLists, execute: fetchFolderLists } =
      collab.DpaIdCollabGraphqlUseFolderLists(
        computed(() => ({
          folderId: props.folderId
        })),
        true
      );

    const { execute: renameFolder } = collab.useMutation(renameFolderQuery);

    const { execute: applyFolderTemplateToFolder } = collab.useMutation(
      applyFolderTemplateToFolderQuery
    );

    const { execute: deleteFolder } = collab.useMutation(deleteFolderQuery);

    const { execute: appendToFolderList } = collab.useMutation(
      appendToFolderListQuery
    );

    const { execute: renameFolderList } = collab.useMutation(
      renameFolderListQuery
    );

    const { execute: deleteFolderItem } = collab.useMutation(
      deleteFolderItemQuery
    );

    const { execute: moveFolderItem } = collab.useMutation(moveFolderItemQuery);

    const { data: dossiers } = collab.useQuery({
      query: getFolderTemplatesQuery
    });

    const {
      fetchValueUnitDetailData,
      valueUnitDetailProps,
      setValueUnitDetail
    } = useValueUnitDetail();

    const valueUnitDetailOpen = ref(false);
    const visualFolderLists = ref(folderLists.value);

    return {
      folders,
      fetchFolders,
      folderLists,
      fetchFolderLists,
      renameFolder,
      deleteFolder,
      appendToFolderList,
      renameFolderList,
      deleteFolderItem,
      moveFolderItem,
      dossiers,
      fetchValueUnitDetailData,
      valueUnitDetailProps,
      setValueUnitDetail,
      applyFolderTemplateToFolder,
      valueUnitDetailOpen,
      visualFolderLists
    };
  },
  components: {
    ChooseFolderTemplateDialog,
    Container,
    Draggable,
    FolderList,
    FolderNavigation,
    UiConfirmationDialog,
    UiDialog,
    UiTextInputDialog,
    UiModal,
    ValueUnitDetailView
  },
  filters: {
    format: (val, arg) => {
      return format(val, arg, { locale });
    }
  },
  props: {
    folderId: {
      type: String,
      required: true
    },
    folderListId: {
      type: String,
      required: false
    },
    action: {
      type: [String, null],
      required: false,
      default: null,
      validator(val) {
        return [
          "addFolderList",
          "chooseFolderTemplate",
          "confirm",
          "confirmDeleteFolderList",
          "edit",
          "editFolderList"
        ].includes(val);
      }
    }
  },
  computed: {
    activeFolder() {
      return this.folders?.find(item => item.id === this.folderId);
    },

    activeFolderId: {
      get() {
        if (this.activeFolder !== undefined) {
          return this.activeFolder.id;
        }

        return null;
      },
      set(val) {
        this.$router.push({ name: "folder", params: { folderId: val } });
      }
    },

    activeFolderListName() {
      if (this.folderListId) {
        return this.folderLists.find(item => item.id === this.folderListId)
          ?.name;
      }
      return undefined;
    },

    activeFolderName() {
      if (this.activeFolder !== undefined) {
        return this.activeFolder.name;
      }

      return null;
    }
  },
  watch: {
    folders(newVal, oldVal) {
      if (newVal.length === 0) {
        this.$router.push({ name: "noFolders" });
      } else if (newVal.find(item => item.id === this.folderId) === undefined) {
        this.$router.push({ name: "folderNotFound" });
      }
    },
    folderLists: {
      immediate: true,
      deep: true,
      handler() {
        this.visualFolderLists = [...this.folderLists];
      }
    },
    delete(newVal, oldVal) {
      if (newVal) {
        this.handleDeleteFolder();
      }
    },
    activeFolderName: {
      immediate: true,
      handler() {
        if (this.activeFolderName) {
          window.document.title = `${this.activeFolderName} | dpa-ID Mappen`;
        }
      }
    }
  },
  methods: {
    /**
     * Goes back to the folder route.
     * @returns {Void}
     */
    showFolder() {
      this.$router.push({ name: "folder" });
    },
    /**
     * Renames the current folder, then closes the dialog
     * @returns {Void}
     */
    handleRenameFolder(newName) {
      this.renameFolder({
        id: this.activeFolderId,
        newName
      })
        .then(data => {
          this.fetchFolders();
          this.showFolder();
        })
        .catch(error => {
      console.error(error); // eslint-disable-line
        });
    },
    handleChooseFolderTemplate(folderTemplateId) {
      this.applyFolderTemplateToFolder({
        folderId: this.folderId,
        folderTemplateId: folderTemplateId
      })
        .then(data => {
          this.fetchFolderLists();
          this.showFolder();
        })
        .catch(error => {
          EventBus.$emit("show", {
            title: `Fehler: Vorlage konnte nicht angewendet werden.`
          });
        console.error(error); // eslint-disable-line
        });
    },
    handleDeleteFolder() {
      // Delete folder and navigate to next folder if available, otherwise redirect to not-found page
      this.deleteFolder({
        id: this.activeFolderId
      })
        .then(data => {
          this.fetchFolders();
          EventBus.$emit("show", {
            title: `Mappe wurde gelöscht.`,
            icon: "delete"
          });
        })
        .then(data => {
          const remainingFolders = this.folders.filter(
            item => item.id !== this.folderId
          );
          const nextFolder = remainingFolders[remainingFolders.length - 1];

          if (!nextFolder) {
            this.$router.push({ name: "noFolders" });
            return;
          }

          this.$router.push({
            name: "folder",
            params: { folderId: nextFolder.id }
          });
        })
        .catch(error => {
      console.error(error); // eslint-disable-line
        });
    },
    handleAddFolderList(newName) {
      this.appendToFolderList({
        folderId: this.activeFolderId,
        folderListId: this.activeFolder.content.id, // always insert at top level
        newFolderItems: [{ name: newName, type: "folderList" }]
      })
        .then(data => {
          this.fetchFolderLists();
          this.showFolder();
        })
        .catch(error => {
        console.error(error); // eslint-disable-line
        });
    },
    handleRenameFolderList(newName) {
      this.renameFolderList({
        folderId: this.activeFolderId,
        folderListId: this.folderListId,
        newName
      })
        .then(data => {
          this.fetchFolderLists();
          this.showFolder();
        })
        .catch(error => {
        console.error(error); // eslint-disable-line
        });
    },
    handleDeleteFolderList(id) {
      this.deleteFolderItem({
        folderId: this.activeFolderId,
        folderItemId: id
      })
        .then(data => {
          this.fetchFolderLists();
          this.showFolder();
          EventBus.$emit("show", {
            title: `Liste wurde gelöscht.`,
            icon: "delete"
          });
        })
        .catch(error => {
        console.error(error); // eslint-disable-line
        });
    },

    currentDraggable(index) {
      // the first or "default" folderList cannot be moved
      return index == 0 ? "div" : "draggable";
    },
    getChildPayload(index) {
      return this.folderLists[index];
    },
    onDrop(dropResult) {
      const { addedIndex, removedIndex, payload } = dropResult;

      // do not allow moving in front of default list
      if (addedIndex === 0) {
        EventBus.$emit("show", { title: "Das geht leider nicht." });
        return;
      }

      if (
        addedIndex !== null &&
        removedIndex !== null &&
        addedIndex !== removedIndex
      ) {
        // remove visually
        this.visualFolderLists.splice(removedIndex, 1);

        // add visually
        this.visualFolderLists.splice(addedIndex, 0, payload);

        // edge case: leave undefined if moved to first position after "default"
        const previousId =
          addedIndex == 1
            ? undefined
            : this.visualFolderLists[addedIndex - 1]?.id;
        const newPreviousFolderItemId = previousId
          ? { newPreviousFolderItemId: previousId }
          : {};

        // move actually
        this.moveFolderItem({
          folderId: this.activeFolderId,
          folderItemId: payload.id,
          newParentFolderListId: this.activeFolder.content.id,
          ...newPreviousFolderItemId
        })
          .then(() => {
            this.fetchFolderLists();
          })
          .catch(error => {
            EventBus.$emit("show", {
              title: `Fehler: Konnte nicht verschoben werden.`
            });
          console.error(error); // eslint-disable-line
          });
      }
    },
    onValueUnitDetailDelete({ folderItemId }) {
      this.closeValueUnitDetail();
      EventBus.$emit("delete-value-unit", { folderItemId });
    },
    closeValueUnitDetail() {
      this.valueUnitDetailOpen = false;
      this.setValueUnitDetail({});
    },
    formatDefaultFolderName
  }
});
</script>

<style scoped>
/* needed to beat specificity of smooth-dnds classes over tailwind */
.smooth-dnd-container.horizontal {
  display: flex;
  position: absolute;
}
@media screen {
  .custom-column-width {
    width: 324px !important;
    min-width: 324px;
  }
}
</style>
